import { CriticScore } from '@/types/codegen-federation'

export interface RatingIcon {
  url?: string
  public_id?: string
  version?: string
}

export interface TheatricalRatingData {
  icon: RatingIcon[]
  rating: string
  author: string
}

/**
 * Maps a theatrical release critic score to a standardized rating format
 */
export const mapTheatricalReleaseCriticToRating = (
  score: CriticScore,
  certifiedFresh?: boolean,
  verifiedHot?: boolean,
): TheatricalRatingData | null => {
  const scoreValue = score?.value ?? score?.floatValue

  if (!scoreValue) {
    return null
  }

  const isImdb = score?.name?.toLowerCase() === 'rating'
  const rating = isImdb ? `${scoreValue}/10` : `${scoreValue}%`

  return {
    icon: [getRatingIcon(score?.name ?? undefined, certifiedFresh, verifiedHot)],
    rating,
    author: score?.name ?? 'Audience',
  }
}

/**
 * Returns the appropriate icon based on score name and special flags
 */
export const getRatingIcon = (scoreName?: string, certifiedFresh?: boolean, verifiedHot?: boolean): RatingIcon => {
  if (!scoreName) {
    return {
      public_id: 'angel-studios/misc-icons/Audience-Score',
      version: '1724953395',
      url: 'http://images.angelstudios.com/image/upload/f_auto/q_auto/v1724953395/angel-studios/misc-icons/Audience-Score',
    }
  }

  const lowerScoreName = scoreName.toLowerCase()

  if (lowerScoreName === 'tomatometer' && certifiedFresh) {
    return {
      public_id: 'angel-studios/misc-icons/Certified-Fresh',
      version: '1724953395',
      url: 'https://images.angelstudios.com/image/upload/f_auto/q_auto/v1724953395/angel-studios/misc-icons/Certified-Fresh',
    }
  }

  if (lowerScoreName === 'audience' && verifiedHot) {
    return {
      public_id: 'angel-studios/misc-icons/verified-hot',
      version: '1724953390',
      url: 'https://images.angelstudios.com/image/upload/v1724953390/angel-studios/misc-icons/verified-hot',
    }
  }

  if (lowerScoreName === 'audience') {
    return {
      public_id: 'angel-studios/misc-icons/Rotten_Tomatoes_positive_audience',
      version: '1760723104',
      url: 'https://images.angelstudios.com/image/upload/v1760723104/angel-studios/misc-icons/Rotten_Tomatoes_positive_audience',
    }
  }

  if (lowerScoreName === 'rating') {
    return {
      public_id: 'angel-studios/misc-icons/171_Imdb_logo_logos-512',
      version: '1752857312',
      url: 'https://images.angelstudios.com/image/upload/v1752857312/angel-studios/misc-icons/171_Imdb_logo_logos-512',
    }
  }

  return {
    public_id: 'angel-studios/misc-icons/rotten-tomatoe',
    version: '1688691818',
    url: 'https://images.angelstudios.com/image/upload/v1688691818/angel-studios/misc-icons/rotten-tomatoe',
  }
}
